import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { saveConsent, useConsent } from "@/lib/consent";

const btn = "lift flex-1 px-4 py-3 font-sub text-sm transition-colors duration-200";

const Category = ({ title, text, checked, onChange, locked, testid }) => (
  <div className="flex items-start justify-between gap-4 border-t border-foreground/10 py-4">
    <div>
      <p className="flex items-center gap-1.5 font-sub text-sm">{locked && <Lock size={12} />} {title}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{text}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={locked} data-testid={testid} aria-label={title} />
  </div>
);

export const CookieBanner = () => {
  const { consent, open } = useConsent();
  const [details, setDetails] = useState(false);
  const [media, setMedia] = useState(consent?.media ?? false);
  const [booking, setBooking] = useState(consent?.booking ?? false);
  useEffect(() => { if (open) { setDetails(true); setMedia(consent?.media ?? false); setBooking(consent?.booking ?? false); } }, [open, consent]);
  if (consent && !open) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[1000] max-h-[calc(100dvh-1rem)] overflow-y-auto p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:bottom-5 md:pb-0 md:max-h-none md:left-5 md:right-auto md:max-w-[460px] md:p-0" role="dialog" aria-modal="false" aria-labelledby="cookie-title" data-testid="cookie-banner">
      <div className="rise border border-foreground/15 bg-shell/95 p-6 shadow-[0_20px_60px_-20px_rgba(16,43,63,0.45)] backdrop-blur-xl">
        <p id="cookie-title" className="flex items-center gap-2 font-display text-xl"><Cookie size={18} className="text-terracotta" /> Kurz zu deiner Privatsphäre</p>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">
          Wir nutzen <strong className="font-medium">kein Tracking und keine Analyse-Tools</strong>. Nötig sind nur Anmeldung, Merkliste und diese Auswahl.
          Karten und Regenradar laden wir von externen Diensten, das Suchfeld für Unterkünfte kommt von Booking.com. Dabei wird deine IP-Adresse übertragen, und Booking.com kann Cookies setzen. Das laden wir nur mit deiner Zustimmung, die du jederzeit unter „Cookie-Einstellungen“ unten auf jeder Seite widerrufen kannst.
        </p>
        {details && (
          <div className="mt-3" data-testid="cookie-details">
            <Category title="Notwendig" text="Anmeldung (Clerk), Merkliste, Standort für die Routenberechnung, diese Auswahl. Immer aktiv." checked locked testid="cookie-necessary-switch" />
            <Category title="Karten & Regenradar" text="Kartenbilder von OpenStreetMap und Radar von RainViewer. Ohne Zustimmung siehst du stattdessen einen Platzhalter." checked={media} onChange={setMedia} testid="cookie-media-switch" />
            <Category title="Unterkunftssuche (Booking.com)" text="Das Suchfeld von Booking.com (u. a. auf Orts-, Insel- und Wochenendseiten). Booking.com kann dabei Cookies setzen. Ohne Zustimmung nutzt du unsere einfache Suche." checked={booking} onChange={setBooking} testid="cookie-booking-switch" />
          </div>
        )}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button onClick={() => saveConsent(false, false)} className={`${btn} bg-ocean text-shell hover:bg-ocean/90`} data-testid="cookie-reject-btn">Nur notwendige</button>
          {details
            ? <button onClick={() => saveConsent(media, booking)} className={`${btn} bg-ocean text-shell hover:bg-ocean/90`} data-testid="cookie-save-btn">Auswahl speichern</button>
            : <button onClick={() => saveConsent(true, true)} className={`${btn} bg-ocean text-shell hover:bg-ocean/90`} data-testid="cookie-accept-btn">Alle akzeptieren</button>}
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          {!details && <button onClick={() => setDetails(true)} className="text-ocean underline underline-offset-4" data-testid="cookie-settings-btn">Einstellungen</button>}
          <span className="ml-auto flex gap-3">
            <Link to="/datenschutz" className="text-muted-foreground underline underline-offset-4" data-testid="cookie-privacy-link">Datenschutz</Link>
            <Link to="/impressum" className="text-muted-foreground underline underline-offset-4" data-testid="cookie-imprint-link">Impressum</Link>
          </span>
        </div>
      </div>
    </div>
  );
};
